import React, { useState, useEffect } from 'react';
import { 
  BanknotesIcon, 
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

function PaymentsManagement() {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    page: 1,
    limit: 20
  });

  useEffect(() => {
    loadPayments();
  }, [filters]);

  const loadPayments = async () => {
    try {
      setIsLoading(true);
      const response = await adminService.getPayments(filters);
      
      if (response.data.success) {
        setPayments(response.data.data.payments);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      toast.error('Ödemeler yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
  };

  const handleProcessPayment = (payment) => {
    setSelectedPayment(payment);
    setShowProcessModal(true);
  };

  const processPayment = async (status, transactionId = '', notes = '') => {
    try {
      setIsProcessing(true);
      
      await adminService.processPayment(selectedPayment.id, {
        status,
        transactionId,
        notes
      });

      toast.success(`Ödeme ${getStatusText(status).toLowerCase()} olarak işaretlendi`);
      setShowProcessModal(false);
      setSelectedPayment(null);
      loadPayments();
    } catch (error) {
      toast.error('Ödeme işlenirken bir hata oluştu');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'processing':
        return <ClockIcon className="w-5 h-5 text-blue-500" />;
      case 'failed':
      case 'cancelled':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Beklemede',
      processing: 'İşleniyor',
      completed: 'Tamamlandı',
      failed: 'Başarısız',
      cancelled: 'İptal Edildi'
    };
    return statusMap[status] || status;
  };

  const getStatusBadge = (status) => {
    const badgeMap = {
      pending: 'badge-warning',
      processing: 'badge-primary',
      completed: 'badge-success',
      failed: 'badge-danger',
      cancelled: 'badge-gray'
    };
    return `badge ${badgeMap[status] || 'badge-gray'}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ödeme Yönetimi</h1>
          <p className="text-gray-600">Kullanıcı ödeme taleplerini yönetin</p>
        </div>
        <div className="mt-4 sm:mt-0 text-right">
          <p className="text-sm text-gray-500">
            Toplam: {pagination.totalItems || 0} ödeme talebi
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="card-body text-center">
            <ClockIcon className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="text-lg font-bold text-yellow-600">
              {payments.filter(p => p.status === 'pending').length}
            </p>
            <p className="text-sm text-gray-600">Bekleyen</p>
          </div>
        </div>

        <div className="card bg-blue-50 border-blue-200">
          <div className="card-body text-center">
            <ClockIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-lg font-bold text-blue-600">
              {payments.filter(p => p.status === 'processing').length}
            </p>
            <p className="text-sm text-gray-600">İşleniyor</p>
          </div>
        </div>

        <div className="card bg-green-50 border-green-200">
          <div className="card-body text-center">
            <CheckCircleIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-lg font-bold text-green-600">
              {payments.filter(p => p.status === 'completed').length}
            </p>
            <p className="text-sm text-gray-600">Tamamlanan</p>
          </div>
        </div>

        <div className="card bg-gray-50 border-gray-200">
          <div className="card-body text-center">
            <CurrencyDollarIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-lg font-bold text-gray-600">
              ${payments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
            </p>
            <p className="text-sm text-gray-600">Toplam Tutar</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <option value="">Tüm Durumlar</option>
              <option value="pending">Beklemede</option>
              <option value="processing">İşleniyor</option>
              <option value="completed">Tamamlandı</option>
              <option value="failed">Başarısız</option>
              <option value="cancelled">İptal Edildi</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" text="Ödemeler yükleniyor..." />
            </div>
          ) : payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-hover w-full">
                <thead>
                  <tr>
                    <th>Kullanıcı</th>
                    <th>Tutar</th>
                    <th>Yöntem</th>
                    <th>Durum</th>
                    <th>Talep Tarihi</th>
                    <th>İşlem Tarihi</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900">{payment.user?.name}</p>
                          <p className="text-sm text-gray-500">{payment.user?.email}</p>
                        </div>
                      </td>
                      <td className="font-medium">${payment.amount?.toFixed(2)}</td>
                      <td className="capitalize">
                        <span className="badge badge-gray">{payment.paymentMethod}</span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(payment.status)}
                          <span className={getStatusBadge(payment.status)}>
                            {getStatusText(payment.status)}
                          </span>
                        </div>
                      </td>
                      <td>{new Date(payment.requestedAt).toLocaleDateString('tr-TR')}</td>
                      <td>
                        {payment.processedAt 
                          ? new Date(payment.processedAt).toLocaleDateString('tr-TR')
                          : '-'
                        }
                      </td>
                      <td>
                        {payment.status === 'pending' && (
                          <button
                            onClick={() => handleProcessPayment(payment)}
                            className="btn btn-primary btn-sm"
                          >
                            İşle
                          </button>
                        )}
                        {payment.transactionId && (
                          <span className="text-xs text-gray-500 block mt-1">
                            {payment.transactionId}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Ödeme talebi bulunamadı</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Sayfa {pagination.currentPage} / {pagination.totalPages} 
                ({pagination.totalItems} toplam ödeme)
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="btn btn-secondary btn-sm"
                >
                  Önceki
                </button>
                <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded text-sm">
                  {pagination.currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="btn btn-secondary btn-sm"
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Process Payment Modal */}
      {showProcessModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Ödeme İşle: {selectedPayment.user?.name}
              </h3>
              
              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Tutar: <strong>${selectedPayment.amount?.toFixed(2)}</strong></p>
                  <p className="text-sm text-gray-600">Yöntem: <strong className="capitalize">{selectedPayment.paymentMethod}</strong></p>
                  <p className="text-sm text-gray-600">Talep Tarihi: <strong>{new Date(selectedPayment.requestedAt).toLocaleDateString('tr-TR')}</strong></p>
                </div>

                {selectedPayment.paymentDetails && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-2">Ödeme Detayları:</p>
                    {selectedPayment.paymentMethod === 'paypal' && (
                      <p className="text-sm text-blue-800">
                        PayPal: {selectedPayment.paymentDetails.paypalEmail}
                      </p>
                    )}
                    {selectedPayment.paymentMethod === 'bank' && (
                      <div className="text-sm text-blue-800 space-y-1">
                        <p>Banka: {selectedPayment.paymentDetails.bankName}</p>
                        <p>Hesap: {selectedPayment.paymentDetails.accountNumber}</p>
                        {selectedPayment.paymentDetails.iban && (
                          <p>IBAN: {selectedPayment.paymentDetails.iban}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                processPayment(
                  formData.get('status'),
                  formData.get('transactionId'),
                  formData.get('notes')
                );
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select name="status" required className="form-select">
                    <option value="">Durum seçin</option>
                    <option value="processing">İşleniyor</option>
                    <option value="completed">Tamamlandı</option>
                    <option value="failed">Başarısız</option>
                    <option value="cancelled">İptal Edildi</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İşlem ID (Opsiyonel)
                  </label>
                  <input
                    type="text"
                    name="transactionId"
                    placeholder="PayPal veya banka işlem numarası"
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notlar (Opsiyonel)
                  </label>
                  <textarea
                    name="notes"
                    rows="3"
                    placeholder="Ödeme ile ilgili notlar..."
                    className="form-textarea"
                  ></textarea>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowProcessModal(false);
                      setSelectedPayment(null);
                    }}
                    className="btn btn-secondary"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="btn btn-primary"
                  >
                    {isProcessing ? 'İşleniyor...' : 'Güncelle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentsManagement;